/**
 * Creator cards and list table shared by Your Feed and Creator Library (OutlierOS feed/explore cards:
 * avatar, name, @handle, INSTAGRAM badge, free-transcripts chip, REELS · PEAK · USUAL VIEWS strip).
 */
import type { ReactNode } from 'react';
import { Rocket, Sparkles } from 'lucide-react';
import { Pill, cn } from '@vw/ui';
import { plural } from '@vw/core/src/plural';
import type { CreatorCard } from '../api';
import { compact, mult } from '../format';
import { Link } from '../router';
import { peakOf, usualViewsOf } from '../creator-sort';
import { CreatorAvatar } from './CreatorAvatar';
import { NicheChips } from './NicheFilter';

export function MetricsStrip({ c }: { c: CreatorCard }) {
  return (
    <div className="grid grid-cols-3 rounded-xl bg-secondary/60 p-3 text-center">
      <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Reels</div><div className="text-sm font-extrabold tabular-nums">{c.reelCount}</div></div>
      <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Peak Wise Score</div><div className="text-sm font-extrabold tabular-nums text-amber-600 dark:text-amber-400">{mult(peakOf(c))}</div></div>
      <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground" title="Median views of their recent reels">Usual views</div><div className="text-sm font-extrabold tabular-nums text-emerald-600 dark:text-emerald-400">{compact(usualViewsOf(c))}</div></div>
    </div>
  );
}

export function FreeTranscriptsChip({ n }: { n: number }) {
  if (!n) return null;
  return (
    <span title="These reels already have transcripts: open them for 0 credits" className="inline-flex items-center gap-1 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
      <Sparkles className="h-3 w-3" />{plural(n, 'free transcript')}
    </span>
  );
}

/** Decision 91: a collab-only profile (stub): only its collab reels are here until its own Reels tab is scanned. */
export const COLLAB_PROFILE_HINT = "Only reels made together with a creator you scanned. Open this profile's Reels tab with the extension to analyse it fully.";
export function CollabProfileChip({ c }: { c: Pick<CreatorCard, 'isStub'> }) {
  if (!c.isStub) return null;
  return <span title={COLLAB_PROFILE_HINT}><Pill tone="violet">Collab profile</Pill></span>;
}

export function CreatorCardView({ c, selected, onSelect, selecting, children, footerExtra, showBio }: {
  c: CreatorCard; selected?: boolean; onSelect?: () => void; selecting?: boolean; children?: ReactNode; footerExtra?: ReactNode; showBio?: boolean;
}) {
  return (
    <article className={cn('group relative flex flex-col gap-4 rounded-2xl border bg-card p-5 shadow-xs transition-shadow hover:shadow-md', selected ? 'border-primary ring-1 ring-primary/40' : 'border-border')}>
      {onSelect && (
        <input type="checkbox" aria-label={`Select @${c.handle}`} checked={!!selected} onChange={onSelect}
          className={cn('absolute right-4 top-4 h-4 w-4 accent-[rgb(var(--primary))] transition-opacity', selecting || selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100')} />
      )}
      <div className="flex items-center gap-3 pr-6">
        <CreatorAvatar url={c.avatarUrl} name={c.handle ?? ''} fullName={c.fullName} />
        <div className="min-w-0">
          <div className="truncate text-sm font-bold">{c.fullName || `@${c.handle}`}{c.isVerified && <span className="ml-1 text-primary" title="Verified">✓</span>}</div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">@{c.handle}<span className="rounded bg-secondary px-1 text-[9px] font-bold uppercase tracking-wider">Instagram</span></div>
        </div>
      </div>
      <FreeTranscriptsChip n={c.transcriptCount} />
      <CollabProfileChip c={c} />
      <NicheChips niches={c.niches} />
      {showBio && c.bio && <p className="line-clamp-2 text-xs text-muted-foreground">{c.bio}</p>}
      <MetricsStrip c={c} />
      {children}
      <div className="mt-auto flex items-center gap-2 border-t border-border pt-4">
        <Link to={`/c/${c.handle}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary-hover">
          <Rocket className="h-3.5 w-3.5" />Open Analysis
        </Link>
        {footerExtra}
      </div>
    </article>
  );
}

export function CreatorTable<T extends CreatorCard>({ rows, columns }: { rows: T[]; columns: Array<{ id: string; label: ReactNode; cell: (c: T) => ReactNode; className?: string }> }) {
  return (
    <div className="overflow-x-auto rounded-3xl border border-border bg-card shadow-xs">
      <table className="w-full text-left text-sm">
        <thead className="bg-secondary/60 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <tr>{columns.map((col) => <th key={col.id} className={cn('px-4 py-3', col.className)}>{col.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((c) => <tr key={c.igPk} className="hover:bg-secondary/30">{columns.map((col) => <td key={col.id} className={cn('px-4 py-3', col.className)}>{col.cell(c)}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

export function CreatorCell({ c }: { c: CreatorCard }) {
  return (
    <div className="flex items-center gap-3">
      <CreatorAvatar url={c.avatarUrl} name={c.handle ?? ''} fullName={c.fullName} size={32} />
      <div className="min-w-0">
        <div className="truncate font-semibold">{c.fullName || `@${c.handle}`}</div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">@{c.handle}<FreeTranscriptsChip n={c.transcriptCount} /><CollabProfileChip c={c} /></div>
      </div>
    </div>
  );
}

export const AnalyzeButton = ({ handle }: { handle: string | null }) => (
  <Link to={`/c/${handle}`} className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"><Rocket className="h-3.5 w-3.5" />Analyze</Link>
);
